import { Component, ViewChild, ElementRef, Input, Output, EventEmitter, OnChanges, AfterViewInit, TemplateRef, ChangeDetectorRef, OnInit } from '@angular/core';
import { ConnectionService } from '@services/connection-service';
import { EventLoggerService } from '@services/event-logger-service';
import { UserService } from '@services/user';
import { PlanName, VariantId, RegimenService } from '@services/regimen-service';
import { AppConfig } from '../../app.config';

interface VariantCosts {
  introductory: number;
  advance: number;
  assist: number;
  pro: number;
}
@Component({
  selector: 'choose-plan',
  templateUrl: './choose-plan.html',
  styles: [':host {@apply tw-block tw-border-t tw-border-t-gray-200;}'],
  standalone: false,
})

export class ChoosePlanComponent implements OnChanges, AfterViewInit, OnInit {
  @ViewChild('scroll') horizontalScroll: ElementRef;
  @ViewChild('introductoryPlan') introductoryPlanTemplate: TemplateRef<any>;
  @ViewChild('advancePlan') advancePlanTemplate: TemplateRef<any>;
  @ViewChild('proPlan') proPlanTemplate: TemplateRef<any>;
  @ViewChild('assistPlan') assistPlanTemplate: TemplateRef<any>;
  @Input('regimen') regimen: any;
  @Output('onSelectPlan') onSelectPlan: EventEmitter<string> = new EventEmitter<string>();
  @Output('onReadMore') onReadMore: EventEmitter<void> = new EventEmitter();
  @Output('hideVariants') hideVariants: EventEmitter<void> = new EventEmitter();
  selectedPlan: string = '';
  user: any;
  showIntroductoryKit: boolean = false;
  isStandAloneVariant: boolean = false; // flag to know whether only 999 variant is available
  showAdvanceTag: boolean = false;
  variantCosts: VariantCosts = {
    introductory: 999,
    advance: 1500,
    pro: 2000,
    assist: 3000,
  };
  defaultRegimenExperiment: Table.Experiment & { variant: { FACE: string, HAIR: string, changeVariantUI?: boolean } };
  planTemplates: Record<PlanName, TemplateRef<any>> = {} as Record<PlanName, TemplateRef<any>>;
  templatesReady: boolean = false;

  constructor(private conn: ConnectionService,
    private readonly eventLogger: EventLoggerService,
    private readonly userService: UserService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    public readonly appConfig: AppConfig,
    private readonly regimenService: RegimenService) {
    this.selectedPlan = this.appConfig.Shared.Regimen.Plans.ADVANCE;
  }

  get isDravidianScriptUser(): boolean {
    return this.userService.dravidianScripts.includes(this.user?.get('languagePreference'));
  }

  get isHairRegimen(): boolean {
    return this.regimen?.class === this.appConfig.Shared.Regimen.Class.HAIR;
  }

  get isTamilUser(): boolean {
    return this.user?.get('languagePreference') === this.appConfig.Shared.Languages.TA;
  }

  async ngOnInit(): Promise<void> {
    this.user = this.conn.getActingUser();
    const experiments = await this.conn.findUserActiveExperiments();
    experiments.forEach((each: any): void => {
      if (each.key === 'advance_tag') this.showAdvanceTag = true;
      if (each.key === 'default_regimen_variant') this.defaultRegimenExperiment = each;
    });
  }

  ngAfterViewInit(): void {
    this.planTemplates = {
      introductory: this.introductoryPlanTemplate,
      advance: this.advancePlanTemplate,
      pro: this.proPlanTemplate,
      assist: this.assistPlanTemplate,
    };
    this.templatesReady = true;
    this.changeDetectorRef.detectChanges();
  }

  ngOnChanges(): void {
    this.showIntroductoryKit = false;
    this.checkForIntroductoryKit();
    this.getPreselectedPlanInfo();
  }

  checkForIntroductoryKit(): void {
    if (!this.regimen.variants) return;
    this.sortVariantsArray();
    this.updateVariantCostsBasedOnLength();
    this.handleTwoVariantScenario();
    this.emitHideVariantsIfNeeded();
  }

  assignVariantCosts(index: number, price: number, length: number = 0): void {
    if (length === 2) {
      if (index === 0) this.variantCosts.introductory = price;
      if (index === 1) this.variantCosts.advance = price;
    } else if (length === 3) {
      if (index === 0) this.variantCosts.advance = price;
      if (index === 1) this.variantCosts.pro = price;
      if (index === 2) this.variantCosts.assist = price;
    } else if (length === 4) {
      if (index === 0) this.variantCosts.introductory = price;
      if (index === 1) this.variantCosts.advance = price;
      if (index === 2) this.variantCosts.pro = price;
      if (index === 3) this.variantCosts.assist = price;
    }
  }

  handleTwoVariantScenario(): void {
    if (this.regimen.variants.length === 2) {
      this.isStandAloneVariant = true;
      this.processIntroductoryKit();
    }
  }

  emitHideVariantsIfNeeded(): void {
    if (!this.showIntroductoryKit && this.regimen.variants.length === 2) {
      this.hideVariants.emit();
    }
  }

  updateVariantCostsBasedOnLength(): void {
    if (this.regimen.variants.length > 3) {
      this.processIntroductoryKit();
    }
    if (this.regimen?.variants.length) {
      const variants = this.regimen?.variants;
      const variantsLength = variants.length;
      for (let index = 0; index < variantsLength; index += 1) {
        this.assignVariantCosts(index, variants[index]?.fixedPrice, variantsLength);
      }
    }
  }

  sortVariantsArray(): void {
    this.regimen.variants.sort((variant1: any, variant2: any): number => variant1.fixedPrice - variant2.fixedPrice);
  }

  processIntroductoryKit(): void {
    // eslint-disable-next-line prefer-destructuring
    const createdAt = this.regimen.createdAt;
    // eslint-disable-next-line prefer-destructuring
    const showAfterDays = 5;
    const dateStr1 = new Date(createdAt);
    const dateStr2 = new Date();

    const daysDifference = this.getDaysDifference(dateStr1, dateStr2);
      if (daysDifference >= showAfterDays) {
      this.showIntroductoryKit = true;
      // If user has selected variants other than 999, for this session, we shouldn't reset the selection
      if (!this.conn.getRegimenVariantSelection()) this.selectPlan(this.appConfig.Shared.Regimen.Plans.INTRODUCTORY, true);
      this.eventLogger.cleverTapEvent('pageOpen', JSON.stringify({ pageName: '999-variant' }));
    }
  }

  getDaysDifference(dateStr1: any, dateStr2: any): number {
    const timeDifference = dateStr2.getTime() - dateStr1.getTime();
    const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;
    const daysDifference = Math.ceil(timeDifference / (MILLISECONDS_PER_DAY));
    return daysDifference;
  }

  getPreselectedPlanInfo(): void {
    const regimenPrice = this.regimen.fixedPrice;
    if (regimenPrice === this.variantCosts.introductory) this.selectedPlan = this.appConfig.Shared.Regimen.Plans.INTRODUCTORY;
    if (regimenPrice === this.variantCosts.advance) this.selectedPlan = this.appConfig.Shared.Regimen.Plans.ADVANCE;
    if (regimenPrice === this.variantCosts.pro) this.selectedPlan = this.appConfig.Shared.Regimen.Plans.PRO;
    if (regimenPrice === this.variantCosts.assist) this.selectedPlan = this.appConfig.Shared.Regimen.Plans.ASSIST;
    setTimeout((): void => {
      if (this.horizontalScroll?.nativeElement) {
        this.scrollToSelectedPlan();
      }
    }, 500);
  }

  selectPlan(plan: string, isAutoSelect: boolean = false): void {
    if (this.selectedPlan === plan) return;
    // Paid user shouldn't be allowed to select the plans.
    const isInternalUser = this.conn.isInternalUser();
    if (this.regimen?.orderPlaced || isInternalUser) return;
    if (!isAutoSelect && !this.conn.getRegimenVariantSelection()) this.conn.setRegimenVariantSelection();
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'upsell_plan_change', variant: plan }));
    this.selectedPlan = plan;
    this.scrollToSelectedPlan();
    this.onSelectPlan.emit(plan);
  }

  scrollToSelectedPlan(): void {
    if (!this.horizontalScroll?.nativeElement) {
      return;
    }

    const elementId = this.getElementIdForPlan(this.selectedPlan);
    if (!elementId) {
      return;
    }

    // Wait for DOM to update, then scroll to the element
    setTimeout((): void => {
      const scrollContainer = this.horizontalScroll.nativeElement;
      const planElement = this.findPlanElement(elementId);

      if (planElement && scrollContainer) {
        const scrollLeft = this.calculateScrollPosition(planElement, scrollContainer);
        this.scrollContainerToPosition(scrollContainer, scrollLeft);
      }
    }, 100); // Delay to ensure DOM is fully updated after plan selection
  }

  private getPlanIdMap(): Record<string, string> {
    return {
      [this.appConfig.Shared.Regimen.Plans.INTRODUCTORY]: this.appConfig.Shared.Regimen.Plans.INTRODUCTORY,
      [this.appConfig.Shared.Regimen.Plans.ADVANCE]: this.appConfig.Shared.Regimen.Plans.ADVANCE,
      [this.appConfig.Shared.Regimen.Plans.PRO]: this.appConfig.Shared.Regimen.Plans.PRO,
      [this.appConfig.Shared.Regimen.Plans.ASSIST]: this.appConfig.Shared.Regimen.Plans.ASSIST,
    };
  }

  private getElementIdForPlan(planName: string): string | undefined {
    const planIdMap = this.getPlanIdMap();
    return planIdMap[planName];
  }

  private findPlanElement(elementId: string): HTMLElement | null {
    if (!this.horizontalScroll?.nativeElement) {
      return null;
    }
    return this.horizontalScroll.nativeElement.querySelector(`#${elementId}`) as HTMLElement;
  }

  private calculateScrollPosition(planElement: HTMLElement, scrollContainer: HTMLElement, leftPadding: number = 8): number {
    const scrollRect = scrollContainer.getBoundingClientRect();
    const elementRect = planElement.getBoundingClientRect();
    const scrollLeft = scrollContainer.scrollLeft + (elementRect.left - scrollRect.left) - leftPadding;
    return Math.max(0, scrollLeft);
  }

  private scrollContainerToPosition(scrollContainer: HTMLElement, scrollLeft: number): void {
    scrollContainer.scrollTo({
      left: scrollLeft,
      behavior: 'smooth',
    });
  }

  readMore(): void {
    const isInternalUser = this.conn.isInternalUser();
    if (isInternalUser) return;
    this.onReadMore.emit();
  }

  private getDefaultPlansToOrder(): PlanName[] {
    return [
      this.appConfig.Shared.Regimen.Plans.ADVANCE,
      this.appConfig.Shared.Regimen.Plans.PRO,
      this.appConfig.Shared.Regimen.Plans.ASSIST
    ];
  }

  private getDefaultRegimenExperimentPlanName(): PlanName | undefined {
    const experimentVariant = this.defaultRegimenExperiment?.variant;
    if (!experimentVariant || !this.regimen?.class || !experimentVariant?.changeVariantUI) {
      return undefined;
    }

    const defaultVariantId = this.defaultRegimenExperiment.variant[this.regimen.class] as VariantId;
    if (!defaultVariantId) {
      return undefined;
    }

    return this.regimenService.getPlanNameByVariantId(defaultVariantId);
  }

  private reorderPlansWithFirst(plans: PlanName[], firstPlan: PlanName): PlanName[] {
    const filtered = plans.filter(plan => plan !== firstPlan);
    return [firstPlan, ...filtered];
  }

  getOrderedPlans(): PlanName[] {
    const plansToOrder = this.getDefaultPlansToOrder();
    const experimentPlan = this.getDefaultRegimenExperimentPlanName();

    if (experimentPlan && plansToOrder.includes(experimentPlan)) {
      return this.reorderPlansWithFirst(plansToOrder, experimentPlan);
    }

    if (this.showIntroductoryKit) {
      return [this.appConfig.Shared.Regimen.Plans.INTRODUCTORY, ...plansToOrder];
    }

    return plansToOrder;
  }

  shouldShowPlan(planName: PlanName): boolean {
    if (planName === this.appConfig.Shared.Regimen.Plans.INTRODUCTORY) {
      return this.showIntroductoryKit;
    }
    if (planName === this.appConfig.Shared.Regimen.Plans.PRO || planName === this.appConfig.Shared.Regimen.Plans.ASSIST) {
      return !this.isStandAloneVariant;
    }
    return true; // advance is always shown (unless hidden by other conditions)
  }
}
